import React, { useRef, useEffect, useState } from 'react';
import { REVEAL_ANIMATION_THRESHOLD } from '@/config';

interface RevealProps {
    children: React.ReactNode;
    className?: string;
    as?: React.ElementType;
    style?: React.CSSProperties;
    // FIX: Add 'id' property to allow passing it to the component.
    id?: string;
}

const Reveal = React.forwardRef<HTMLElement, RevealProps>(
    // FIX: Destructure 'id' from props.
    ({ children, className = '', as: Component = 'div', style, id }, ref) => {
    
    const internalRef = useRef<HTMLElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Use a callback ref to manage both internal and forwarded refs
    const setRefs = (node: HTMLElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };
    
    useEffect(() => {
        const element = internalRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(element);
                }
            },
            {
                threshold: REVEAL_ANIMATION_THRESHOLD,
                rootMargin: '0px 0px -50px 0px',
            }
        );

        observer.observe(element);

        return () => {
            // disconnect() is safer than unobserve() in cleanup — handles edge cases
            // where the element might have been removed from the DOM
            observer.disconnect();
        };
    }, []);

    const combinedClassName = `reveal ${className} ${isVisible ? 'visible' : ''}`.trim();
    const TypedComponent = Component as any;

    return (
        // FIX: Pass the 'id' prop to the rendered component.
        <TypedComponent ref={setRefs} className={combinedClassName} style={style} id={id}>
            {children}
        </TypedComponent>
    );
});

Reveal.displayName = 'Reveal';

export default Reveal;